import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { Login } from './pages/auth/Login';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { SetPassword } from './pages/auth/SetPassword';
import { CoachDashboard } from './pages/coach/CoachDashboard';
import { Athletes } from './pages/coach/Athletes';
import { AthleteProfile } from './pages/coach/AthleteProfile';
import { PlanBuilder } from './pages/coach/PlanBuilder';
import { CoachingCalls } from './pages/coach/CoachingCalls';
import { Recruiting } from './pages/coach/Recruiting';
import { AthleteRecruiting } from './pages/athlete/Recruiting';
import { AthleteDashboard } from './pages/athlete/AthleteDashboard';
import { RaceCalendar } from './pages/athlete/RaceCalendar';
import { Billing } from './pages/athlete/Billing';
import { ParentView } from './pages/parent/ParentView';
import { ParentBilling } from './pages/parent/ParentBilling';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { CoachSettings } from './pages/coach/CoachSettings';
import { CoachDirectory } from './pages/admin/CoachDirectory';
import { CoachProfileAdmin } from './pages/admin/CoachProfileAdmin';
import { ProDashboard } from './pages/pro/ProDashboard';
import { AthleteSettings } from './pages/athlete/AthleteSettings';
import { ProAthletes } from './pages/admin/ProAthletes';
import { ProAthleteDetail } from './pages/admin/ProAthleteDetail';
import { Messages } from './pages/coach/Messages';
import { SafetyReview } from './pages/admin/SafetyReview';

function RootRedirect() {
  const { user, profile, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile) return <Navigate to={profile.role === 'admin' ? '/coach/dashboard' : `/${profile.role}/dashboard`} replace />;
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />

      <Route
        path="/coach/dashboard"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <CoachDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/athlete/dashboard"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <AthleteDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/dashboard"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <ProDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/races"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <RaceCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/standards"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <ProDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/gear"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <ProDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro/profile"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <ProDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parent/dashboard"
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/coach/athletes"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <Athletes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/athletes/:id"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <AthleteProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/settings"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <CoachSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/plan-builder"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <PlanBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/calls"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <CoachingCalls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/recruiting"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <Recruiting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/messages"
        element={
          <ProtectedRoute allowedRoles={['coach', 'admin']}>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/safety-review"
        element={
          <ProtectedAdminRoute>
            <SafetyReview />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/athlete/recruiting"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <AthleteRecruiting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/athlete/races"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <RaceCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/athlete/billing"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parent/billing"
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedAdminRoute>
            <AdminDashboard />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/admin/coaches"
        element={
          <ProtectedAdminRoute>
            <CoachDirectory />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/admin/coaches/:id"
        element={
          <ProtectedAdminRoute>
            <CoachProfileAdmin />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/admin/pro-athletes"
        element={
          <ProtectedAdminRoute>
            <ProAthletes />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/admin/pro-athletes/:id"
        element={
          <ProtectedAdminRoute>
            <ProAthleteDetail />
          </ProtectedAdminRoute>
        }
      />
      <Route
        path="/athlete/settings"
        element={
          <ProtectedRoute allowedRoles={['athlete']}>
            <AthleteSettings />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
